import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  ErrorData,
  ErrorHistory,
  ErrorCategory,
  categorizeError,
} from '../models/error-handling.model';
import { Router, NavigationStart } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class ErrorHandlingService implements OnDestroy {
  private latestErrorSubject = new BehaviorSubject<ErrorData | null>(null);
  private errorHistorySubject = new BehaviorSubject<ErrorHistory>({
    latestError: null,
    errorStack: [],
    filteredErrors: [],
  });

  private subscriptions = new Set<Subscription>();

  constructor(private readonly router: Router) {
    const routeSub = this.router.events.subscribe((ev) => {
      if (ev instanceof NavigationStart) {
        // Clear errors when navigating to a new page
        this.clearErrors();
      }
    });
    this.subscriptions.add(routeSub);
  }

  // Public API
  getLatestError(): Observable<ErrorData> {
    return new Observable<ErrorData>((observer) => {
      const sub = this.latestErrorSubject.subscribe((err) => {
        const currentContext = this.router.url;
        // Only emit errors that belong to current route context
        if (err && (!err.contextId || err.contextId === currentContext)) {
          observer.next(err);
        }
      });
      // Auto cleanup when unsubscribed
      sub.add(() => this.subscriptions.delete(sub));
      this.subscriptions.add(sub);
      return () => sub.unsubscribe();
    });
  }

  clearErrors(): void {
    const history = this.errorHistorySubject.value;
    history.latestError = null;
    history.errorStack = [];
    history.filteredErrors = [];
    this.errorHistorySubject.next({ ...history });
    // Also clear latest
    this.latestErrorSubject.next(null);
    console.log('this.latestErrorSubject', this.latestErrorSubject.value);
    console.log('this.errorHistorySubject', this.errorHistorySubject.value);
  }

  filterErrors(type: string): ErrorData[] {
    const history = this.errorHistorySubject.value;
    const predicate = (e: ErrorData) => {
      if (type === 'Client')
        return categorizeError(e.statusCode) === ErrorCategory.Client;
      if (type === 'Server')
        return categorizeError(e.statusCode) === ErrorCategory.Server;
      if (type === 'Network')
        return categorizeError(e.statusCode) === ErrorCategory.Network;
      return true;
    };
    const filtered = history.errorStack.filter(predicate);
    history.filteredErrors = filtered;
    this.errorHistorySubject.next({ ...history });
    return filtered;
  }

  subscribeToErrors(callback: (error: ErrorData) => void): Subscription {
    const sub = this.getLatestError().subscribe(callback);
    // Track and auto-clean inactive subscriptions
    sub.add(() => this.subscriptions.delete(sub));
    this.subscriptions.add(sub);
    return sub;
  }

  // Process new error from interceptor or other sources
  processError(error: ErrorData): void {
    const currentContext = this.router.url;
    // Only process if error belongs to current route context
    if (error.contextId && error.contextId !== currentContext) {
      // Still log externally for diagnostics
      this.logError(error);
      return;
    }

    const history = this.errorHistorySubject.value;
    history.latestError = error;
    history.errorStack = [...history.errorStack, error];
    this.errorHistorySubject.next({ ...history });
    this.latestErrorSubject.next(error);

    // Log
    this.logError(error);
  }

  // Logging to console and external analytics (Clarity)
  logError(error: ErrorData): void {
    const ts = error.timestamp;
    const sc = error.statusCode;
    const ep = error.endpoint;
    const msg = this.getUserFriendlyMessage(error) || error.message;
    // Console log
    // eslint-disable-next-line no-console
    console.error(`[ERROR] ${ts}  | ${sc}  | ${ep}  | ${msg}`);
  }

  // Translate to user-friendly message
  getUserFriendlyMessage(error: ErrorData): string {
    if (error?.message?.length > 0) return error.message;

    if (error.statusCode >= 500)
      return 'Server error occurred. Please try again later.';
    if (error.statusCode === 404) return 'Requested resource not found.';
    if (error.statusCode === 403)
      return 'You do not have permission for this action.';
    if (error.statusCode === 401)
      return 'Your session has expired. Please login again.';
    if (error.statusCode === 400)
      return 'Bad request. Please check the input data.';
    if (error.statusCode === 0)
      return 'Network error. Check your internet connection.';
    return 'An unexpected error occurred.';
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions.clear();
  }
}
